import React, { useEffect, useMemo, useState } from "react";
import { t } from "ttag";

import { TsicommonCmsCompareFeatureOrNull } from "@reactivated";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { Link } from "../../../common/Link";
import { GroupedTileModelsType } from "../../../templates/ShopTileTemplate";
import { getOptionValueHash } from "../../../utils/catalogue";
import { useCurrentBreakpoint } from "../../../utils/hooks";
import { sortProductOptions } from "../../../utils/sorting";
import { useIsSSR } from "../../../utils/ssr";
import { CompareFeatures } from "../CompareFeatures";
import { CompareTile } from "../CompareTile";
import { BlankCompareTile } from "../CompareTile/BlankCompareTile";
import { CompareTileType } from "../ShopCompareTypes";
import { ModelSelector } from "../elements/ModelSelector";

import styles from "./index.module.scss";

interface ICompareGridProps {
    compareTiles: CompareTileType[];
    compareFeatures: TsicommonCmsCompareFeatureOrNull[];
    handleSelectProduct: (newTile: number, column: number) => void;
    selectedProducts: number[];
    groupedDropdownModels: GroupedTileModelsType;
}

export const CompareGrid = (props: ICompareGridProps) => {
    const isSSR = useIsSSR();
    const currentBreakpoint = useCurrentBreakpoint();
    const isBelowLaptop = !isSSR && currentBreakpoint.belowLaptop;

    const [selectedCompareTiles, setSelectedCompareTiles] = useState<
        (CompareTileType | null)[]
    >([]);

    const maxCompareTiles = useMemo(() => {
        return isBelowLaptop ? 2 : 3;
    }, [isBelowLaptop]);

    const selectedProducts = useMemo(() => {
        return props.selectedProducts?.slice(0, maxCompareTiles);
    }, [props.selectedProducts, maxCompareTiles]);

    const allSizeOptions = useMemo(() => {
        return props.compareTiles
            .map((tile) => tile.size_options)
            .filter((arr): arr is string[] => Array.isArray(arr));
    }, [props.compareTiles]);

    const availableSizes = useMemo(() => {
        if (allSizeOptions.length === 0) return [] as string[];
        const allSizes = allSizeOptions.reduce((acc, curr) => {
            return [...acc, ...curr];
        }, [] as string[]);
        const commonSizes = [...new Set(allSizes)];
        return sortProductOptions("option_size", commonSizes);
    }, [allSizeOptions]);

    const defaultSelected = useMemo(() => {
        if (availableSizes.length > 0) {
            return availableSizes[0];
        }
        return "";
    }, [availableSizes]);

    const [selectedSize, setSelectedSize] = useState<string>(defaultSelected);

    useEffect(() => {
        if (selectedProducts) {
            const uniqueProductIds = Array.from(new Set(selectedProducts));
            const uniqueTiles = uniqueProductIds.map(
                (productId) =>
                    props.compareTiles.find((t) => t.product === productId) ||
                    null,
            );
            setSelectedCompareTiles(uniqueTiles);
            return;
        }
    }, [selectedProducts, props.compareTiles]);

    useEffect(() => {
        setSelectedSize(defaultSelected);
    }, [defaultSelected]);

    const selectID = "compare-size-selector";
    const onSubmit = (e: React.FormEvent<HTMLFormElement>) =>
        e.preventDefault();
    const onSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedSize(e.target.value);
    };

    const selectedDisplayTiles: (CompareTileType | null)[] = useMemo(() => {
        if (selectedCompareTiles.length < maxCompareTiles) {
            const blankTiles = Array(
                maxCompareTiles - selectedCompareTiles.length,
            ).fill(null);
            return [...selectedCompareTiles, ...blankTiles];
        }
        return selectedCompareTiles.slice(0, maxCompareTiles);
    }, [selectedCompareTiles, maxCompareTiles]);

    const SizeSelector = () => {
        if (availableSizes.length === 0) return null;

        return (
            <form className={styles.sizeSelector} onSubmit={onSubmit}>
                <label htmlFor={selectID}>{t`Price by mattress size`}</label>
                <span className={styles.selectWrapper}>
                    <select
                        id={selectID}
                        aria-label={t`Select mattress size`}
                        className="al-compareresults-selectsize"
                        onChange={onSelectChange}
                        value={selectedSize}
                    >
                        {availableSizes.map((option) => (
                            <option
                                key={getOptionValueHash(option)}
                                value={option}
                            >
                                {option}
                            </option>
                        ))}
                    </select>
                </span>
            </form>
        );
    };

    return (
        <div>
            <div className={styles.headerContainer}>
                <h1>{t`Compare All Mattress Models`}</h1>
                {!isBelowLaptop && <SizeSelector />}
            </div>
            {isBelowLaptop && <SizeSelector />}
            <div className={styles.stickyModelSelector}>
                <div className={styles.stickyModelSelectorContainer}>
                    {selectedDisplayTiles.map((tile, index) =>
                        tile == null ? (
                            <ModelSelector
                                id="compare_tile_model_empty"
                                key={`compare_tile_model_${index}`}
                                label="Select Model"
                                value={null}
                                onChange={(newTile: number) => {
                                    props.handleSelectProduct(newTile, index);
                                }}
                                groupedModels={props.groupedDropdownModels}
                                selectedProducts={selectedProducts}
                                placeholder="Select Model"
                            />
                        ) : (
                            <ModelSelector
                                id={`compare_tile_model_${tile.product}`}
                                key={`compare_tile_model_${index}`}
                                label={tile.title || ""}
                                value={tile.product || null}
                                onChange={(newTile: number) =>
                                    props.handleSelectProduct(newTile, index)
                                }
                                groupedModels={props.groupedDropdownModels}
                                selectedProducts={selectedProducts}
                            />
                        ),
                    )}
                </div>
            </div>
            <div className={styles.compareTileContainer}>
                <div className={styles.compareTiles}>
                    {selectedDisplayTiles.map((tile, index) =>
                        tile === null ? (
                            <BlankCompareTile
                                key={`blank-${index}`}
                                handleSelectProduct={(newTile: number) =>
                                    props.handleSelectProduct(newTile, index)
                                }
                                selectedProducts={selectedProducts}
                                groupedDropdownModels={
                                    props.groupedDropdownModels
                                }
                            />
                        ) : (
                            <CompareTile
                                key={`tile-${tile.id || index}`}
                                tile={tile}
                                handleSelectProduct={(newTile: number) =>
                                    props.handleSelectProduct(newTile, index)
                                }
                                selectedProducts={selectedProducts}
                                selectedSize={selectedSize}
                                groupedDropdownModels={
                                    props.groupedDropdownModels
                                }
                            />
                        ),
                    )}
                </div>
            </div>
            <div className={styles.compareFeatures}>
                <CompareFeatures
                    features={props.compareFeatures}
                    compareTileFeatures={selectedDisplayTiles.map(
                        (tile) =>
                            tile?.compare_features?.map((cf) => cf.value) || [],
                    )}
                />
                <div className={styles.shopNowButtonContainer}>
                    {selectedDisplayTiles.map((tile, index) =>
                        tile !== null ? (
                            (() => {
                                const sizePriceMap = tile?.size_price_map;
                                const isSelectedSizeAvailable =
                                    selectedSize &&
                                    sizePriceMap &&
                                    selectedSize in sizePriceMap;

                                return isSelectedSizeAvailable ? (
                                    <Link
                                        key={`shop-now-button-compare-footer-${index}`}
                                        href={tile.link}
                                        className="button al-comparisongrid-shopnow"
                                        id={`shop-now-button-compare-footer-${index}`}
                                    >
                                        {t`Shop Now`}
                                        <span className="ada-screenreader-only">
                                            {interpolate(
                                                t`for %(productName)s`,
                                                {
                                                    productName:
                                                        tile.title || "",
                                                },
                                            )}
                                        </span>
                                    </Link>
                                ) : (
                                    <button
                                        key={`shop-now-button-compare-footer-${index}`}
                                        className="button al-comparisongrid-shopnow"
                                        disabled
                                    >
                                        {t`Unavailable`}
                                    </button>
                                );
                            })()
                        ) : (
                            <span
                                key={`shop-now-button-compare-footer-${index}`}
                            ></span>
                        ),
                    )}
                </div>
            </div>
        </div>
    );
};
